import React, { useState, useEffect } from 'react';

import { Box } from '@wds/box';
import { Layout } from '@wds/layout';
import { Tab, Tabs } from '@wds/tabs';
import { Typography } from '@wds/typography';
import { Input } from '@wds/input';
import { Button } from '@wds/button';
import { Fade, Zoom } from '@wds/motion';
import { InputGroup } from '@wds/input-group';
import { InputMessage } from '@wds/input-message';

import PropTypes from 'prop-types';
import ClientCaptcha from 'react-client-captcha';
import ReCAPTCHA from 'react-google-recaptcha';
import STRINGS from '../constants';

import PasswordField from './components/PasswordField';
import CustomField from '../../sma.common/components/react/CustomField/CustomField';

const RegistrationPageContext = (props) => {
	const {
		tabs,
		handleTabChange,
		selectedTab,
		siteDetails,
		reCaptchaKey,
		showCaptcha,
		registrationCustomFields,
		csrfToken,
		showRegisterWithGoogle,
		googleUrl,
	} = props;

	const captchaCharLength = 6;

	const defaultContext = {
		first_name: '',
		last_name: '',
		email: '',
		password: '',
		registrant_type: 'individual',
		organization_name: '',
		tax_number: '',
		recaptcha_value: '',
	};

	const [context, setContext] = useState(defaultContext);
	const [isLoaded, setIsLoaded] = useState(false);
	const [errors, setErrors] = useState({});
	const [customFields, setCustomFields] = useState([]);
	const [isPasswordValid, setIsPasswordValid] = useState(false);
	const [nextUrl, setNextUrl] = useState('');
	const [language] = useState(window.LOCALE?.language || 'en');
	const [captchaCode, setCaptchaCode] = useState('');

	useEffect(() => {
		const fields = [];
		registrationCustomFields.forEach((field) => {
			fields.push({
				...field,
				value: field.default || '',
			});
		});
		setCustomFields(fields);
	}, []);

	useEffect(() => {
		setIsLoaded(true);
		getNextUrl();
	}, []);

	useEffect(() => {
		let registrantType;
		if (selectedTab === 'individualTab') {
			registrantType = 'individual';
		} else {
			registrantType = 'organization';
		}
		setContext({ ...context, registrant_type: registrantType });
	}, [selectedTab]);

	const requestRegister = () => {
		const url = reverse('api-internal-register');
		const body = {
			first_name: context.first_name,
			last_name: context.last_name,
			email: context.email,
			password: context.password,
			registrant_type: context.registrant_type,
			recaptcha_value: context.recaptcha_value,
			next: nextUrl,
		};
		if (context.registrant_type === 'organization') {
			body.organization_name = context.organization_name;
			body.tax_number = context.tax_number;
		}
		if (context.registrant_type === 'individual') {
			body.custom_fields = customFields.filter((field) => field.target === 'applicant');
		} else {
			body.custom_fields = customFields;
		}
		fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRFToken': csrfToken,
			},
			body: JSON.stringify(body),
		})
			.then((response) => response.json())
			.then((data) => {
				if (data.success === true) {
					window.location.href = data.redirect_url;
				} else {
					setErrors(data.error);
				}
			})
			.catch((error) => {
				console.error(`an error has occurred: ${error}`);
				setErrors({ form: STRINGS.unknownError });
			});
	};

	const getNextUrl = () => {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has('next')) {
			setNextUrl(urlParams.get('next'));
		}
	};

	function validateForm() {
		const formErrors = errors;
		delete formErrors.recaptcha;
		delete formErrors.form;
		const isValidFirstName = (name) => /^[\p{L}\s'-]+$/u.test(name);
		const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
		const isCaptchaValid = (captcha) => {
			if (siteDetails.alternateCaptcha) {
				// Check if the captcha matches the generated code in ClientCaptcha
				return (captcha.length === captchaCharLength) && (captchaCode === context.recaptcha_value);
			}
			// For ReCAPTCHA, just check if the value is present, the validation happens on the server side
			return captcha && captcha.length > 0;
		};

		const validateField = (field, value, errorMessage) => {
			if (value) {
				delete formErrors[field];
			} else {
				formErrors[field] = errorMessage;
			}
		};

		validateField('first_name', isValidFirstName(context.first_name), STRINGS.fieldErrors.invalidFirstName);
		validateField('last_name', isValidFirstName(context.last_name), STRINGS.fieldErrors.invalidLastName);
		validateField('email', isValidEmail(context.email), STRINGS.fieldErrors.invalidEmail);
		validateField('password', isPasswordValid, STRINGS.fieldErrors.invalidPassword);
		validateField(
			'organization_name',
			context.registrant_type !== 'organization' || context.organization_name,
			STRINGS.fieldErrors.invalidOrganizationName,
		);
		validateField(
			'recaptcha_value',
			!showCaptcha || isCaptchaValid(context.recaptcha_value),
			STRINGS.fieldErrors.invalidCaptcha,
		);
		validateField(
			'tax_number',
			(context.registrant_type !== 'organization' || !siteDetails.require_tax_number || context.tax_number),
			STRINGS.fieldErrors.invalidTaxNumber,
		);
		customFields.forEach((field) => {
			if (field.required && (!field.value || field.value.length === 0)) {
				if (
					(context.registrant_type === 'individual' && field.target === 'applicant')
					|| context.registrant_type === 'organization') {
					formErrors[field.id] = `${field.name} is required`;
				}
			} else {
				delete formErrors[field.id];
			}
		});
		setErrors(Object.keys(formErrors).length ? { ...formErrors } : {});
		if (!Object.keys(formErrors).length) {
			requestRegister();
		}
	}

	const onHandleContextChange = (key) => (e) => {
		setContext({ ...context, [key]: e.target.value });
	};

	const handlePasswordChange = (e) => {
		setContext({ ...context, password: e.target.value });
	};

	const onHandleRegister = () => {
		validateForm();
	};

	const onHandleGoogle = () => {
		window.location.href = googleUrl;
	};

	const handleReCaptchaChange = (value) => {
		setContext({ ...context, recaptcha_value: value });
	};

	const handleCustomFieldChange = (id) => (value) => {
		const updatedFields = customFields.map((field) => {
			if (field.id === id) {
				return { ...field, value };
			}
			return field;
		});
		setCustomFields(updatedFields);
		setContext({
			...context,
			custom_fields: customFields.map((field) => {
				if (field.id === id) {
					return { ...field, value };
				}
				return field;
			}),
		});
	};

	return (
		<Box
			display='flex'
			justifyContent='center'
			alignItems='center'
			style={{ minHeight: '90vh' }}
			role='main'
			aria-labelledby='registration-page-title'
		>
			<Box
				display='flex'
				justifyContent='space-around'
			>
				<Zoom in={isLoaded} alwaysRenderContent>
					<Layout direction='vertical'>
						<Box>
							<Box mb={4}>
								<Box style={{ width: '480px', margin: '1.5rem 0px' }}>
									<img
										className='sma-logo-powered'
										src='/media/img/smapply/surveymonkey-apply.png'
										alt='SurveyMonkey Apply logo'
										style={{ maxWidth: '330px' }}
									/>
								</Box>
							</Box>
						</Box>
						<Box display='flex' justifyContent='center' flexDirection='column' alignItems='center'>
							<Typography
								variant='sectionTitleSm'
								id='registration-page-title'
							>
								{STRINGS.registrationPage.registrationPageTitle}
							</Typography>
							<Box
								display='flex'
								justifyContent='space-around'
								gap={2}
								p={4}
							>
								<Typography
									variant='body'
								>
									{STRINGS.registrationPage.alreadyHaveAccount}
								</Typography>
								<Typography
									variant='link'
									component='a'
									href='/acc/l/'
									id='login-link'
									aria-label='Login to your account'
								>
									{STRINGS.login}
								</Typography>
							</Box>
						</Box>
						<Box
							style={{
								width: '620px',
								backgroundColor: 'white',
								margin: 'auto',
							}}
							borderColor='muted'
							py='2'
							px='6'
							display='flex'
							justifyContent='center'
							flexDirection='column'
							role='region'
							aria-labelledby='registration-form-title'
						>
							<Box
								display='flex'
								justifyContent='center'
								style={{ border: '1px #D0D2D3', borderStyle: 'none none solid none' }}
							>
								<Tabs onChange={handleTabChange} aria-label='Registration tabs'>
									{Object.keys(tabs).map((tab) => {
										const tabData = tabs[tab];
										return (
											<Tab
												key={tabData.id}
												selected={tabData.active}
												title={tabData.label}
												value={tabData.id}
												aria-selected={tabData.active}
											/>
										);
									})}
								</Tabs>
							</Box>
							{siteDetails?.registrationInstructions?.[language] && (
								<Box display='flex' flexDirection='column' alignItems='center' mt={4} mb={2} gap={2}>
									<Box>
										<Typography variant='bodySm' weight='medium' style={{ fontSize: '15px' }}>
											{STRINGS.instructions}
										</Typography>
									</Box>
									<Typography align='center' variant='bodySm'>
										{siteDetails.registrationInstructions[language]
											.split('\n')
											.map((line, index) => (
												// eslint-disable-next-line react/no-array-index-key
												<React.Fragment key={`instruction-line-${index}`}>
													{line}
													<br />
												</React.Fragment>
											))}
									</Typography>
								</Box>
							)}
							{errors.form && (
								<Box
									p={4}
									mt={2}
									style={{
										backgroundColor: '#AC145A',
										borderRadius: '5px',
										color: '#FFFFFF',
									}}
									role='alert'
									aria-live='assertive'
								>
									{errors.form}
								</Box>
							)}
							<form onSubmit={onHandleRegister} aria-labelledby='registration-form-title'>
								<Box
									style={{ margin: '2rem auto' }}
									display='flex'
									flexDirection='column'
									gap={4}
								>
									<Box display='flex' style={{ width: '100%' }} gap={4}>
										<Box style={{ width: '100%' }}>
											<InputGroup
												label={STRINGS.registrationPage.firstName}
												stretched
											>
												<Input
													value={context.first_name}
													onChange={onHandleContextChange('first_name')}
													id='firstname-field'
													autoComplete='given-name'
													aria-required='true'
												/>
											</InputGroup>
											{errors?.first_name && (
												<InputMessage color='error' role='alert'>
													{errors.first_name}
												</InputMessage>
											)}
										</Box>
										<Box style={{ width: '100%' }}>
											<InputGroup
												label={STRINGS.registrationPage.lastName}
												stretched
											>
												<Input
													value={context.last_name}
													onChange={onHandleContextChange('last_name')}
													id='lastname-field'
													stretched
													autoComplete='family-name'
													aria-required='true'
												/>
											</InputGroup>
											{errors?.last_name && (
												<InputMessage color='error' role='alert'>
													{errors.last_name}
												</InputMessage>
											)}
										</Box>
									</Box>
									<Fade in={selectedTab === 'organizationTab'} speed='slow'>
										<Box
											display='flex'
											flexDirection='column'
											gap={2}
										>
											<Box>
												<InputGroup
													label={STRINGS.registrationPage.orgName}
													stretched
												>
													<Input
														value={context.organization_name}
														onChange={onHandleContextChange('organization_name')}
														id='org-name-field'
														stretched
														autoComplete='off'
														aria-required={selectedTab === 'organizationTab'}
													/>
												</InputGroup>
												{errors?.organization_name && (
													<InputMessage color='error' role='alert'>
														{errors.organization_name}
													</InputMessage>
												)}
											</Box>
											<Box>
												<InputGroup
													label={`${STRINGS.registrationPage.taxNumber} ${siteDetails.require_tax_number ? '' : `(${STRINGS.optional})`}`}
													stretched
												>
													<Input
														value={context.tax_number}
														onChange={onHandleContextChange('tax_number')}
														id='tax-number-field'
														stretched
														autoComplete='off'
														required={siteDetails.require_tax_number}
													/>
												</InputGroup>
												{errors?.tax_number && (
													<InputMessage color='error' role='alert'>
														{errors.tax_number}
													</InputMessage>
												)}
											</Box>
										</Box>
									</Fade>
									<Box>
										<InputGroup
											label={STRINGS.registrationPage.email}
											stretched
										>
											<Input
												value={context.email}
												onChange={onHandleContextChange('email')}
												id='email-field'
												stretched
												type='email'
												autoComplete='email'
												aria-required='true'
											/>
										</InputGroup>
										{errors?.email && (
											<InputMessage color='error' role='alert'>
												{errors.email}
											</InputMessage>
										)}
									</Box>
									<Box id='custom-fields' display='flex' gap={2} flexDirection='column'>
										{registrationCustomFields.filter(
											(field) => (context.registrant_type === 'individual' && field.target === 'applicant')
												|| context.registrant_type === 'organization',
										).map((field) => (
											<CustomField
												onChange={handleCustomFieldChange(field.id)}
												key={`custom-field-${field.id}`}
												id={field.id}
												label={field.name}
												value={field.value || field.default}
												name={field.name}
												type={field.type}
												required={field.required}
												choices={field.choices}
												errors={errors}
												setErrors={setErrors}
												helperText={field.helper_text ? field.helper_text : ''}
												defaultValue={field.default}
												aria-required={field.required}
												aria-invalid={!!errors[field.id]}
												aria-describedby={errors[field.id] ? `${field.id}-error` : undefined}
											/>
										))}
									</Box>
									<Box>
										<PasswordField
											label={STRINGS.registrationPage.password}
											onChange={handlePasswordChange}
											size='md'
											setIsPasswordValid={setIsPasswordValid}
											aria-required='true'
										/>
										{!isPasswordValid && errors?.password && (
											<InputMessage color='error' role='alert'>
												{errors.password}
											</InputMessage>
										)}
									</Box>
									<p style={{ fontSize: 'small' }}>
										{STRINGS.registrationPage.termsAndPolicy}
										<a
											href={reverse('tos-url')}
											style={{ color: '#007FAA', margin: 'auto 2px' }}
											aria-label='Terms of Service'
										>
											{` ${STRINGS.registrationPage.termsOfService} `}
										</a>
										{STRINGS.registrationPage.and}
										<a
											href={reverse('privacy-url')}
											style={{ color: '#007FAA', margin: 'auto 2px' }}
											aria-label='Privacy Policy'
										>
											{` ${STRINGS.registrationPage.privacyPolicy}`}
											.
										</a>
									</p>
									{showCaptcha && (
										siteDetails.alternateCaptcha ? (
											<Box display='flex' flexDirection='column' gap={4}>
												<ClientCaptcha
													captchaCode={(code) => setCaptchaCode(code)}
													charsCount={captchaCharLength}
													height={60}
													width={200}
													aria-label='Captcha verification'
												/>
												<Box>
													<Input
														value={context.recaptcha_value}
														onChange={onHandleContextChange('recaptcha_value')}
														id='captcha-field'
														errorMessage={errors?.recaptcha_value}
														color={errors?.captcha ? 'error' : undefined}
														aria-required='true'
													/>
												</Box>
											</Box>
										) : (
											<>
												<ReCAPTCHA
													sitekey={reCaptchaKey}
													onChange={(code) => handleReCaptchaChange(code)}
													aria-label='ReCAPTCHA verification'
												/>
												{errors?.recaptcha && (
													<InputMessage color='error' role='alert'>
														{STRINGS.recaptchaVerificationFailed}
													</InputMessage>
												)}
											</>
										))}
									<Box>
										<Button
											id='register-button'
											size='lg'
											onClick={onHandleRegister}
											stretched
											aria-label='Create account'
										>
											{STRINGS.registrationPage.createAccount}
										</Button>
									</Box>
									{showRegisterWithGoogle && (
										<Box display='flex' flexDirection='column' gap={4} alignItems='center'>
											<Box display='flex'>
												<Box style={{
													width: '250px',
													border: '1px #D0D2D3',
													borderStyle: 'solid none none none',
													marginTop: '0.85rem',
												}}
												/>
												<Box mx={3}>
													<Typography variant='cardTitle'>{STRINGS.or}</Typography>
												</Box>
												<Box style={{
													width: '250px',
													border: '1px #D0D2D3',
													borderStyle: 'solid none none none',
													marginTop: '0.85rem',
												}}
												/>
											</Box>
											<Button
												color='alt'
												size='md'
												label='google signin'
												onClick={() => onHandleGoogle()}
												aria-label='Register with Google'
											>
												<img
													src='/media/img/icons/logo-google-g.png'
													width='25px'
													height='auto'
													alt='Google logo'
												/>
											</Button>
											<Typography
												variant='caption'
											>
												{STRINGS.registrationPage.registerWithGoogle}
											</Typography>
										</Box>
									)}
								</Box>
							</form>
						</Box>
					</Layout>
				</Zoom>
			</Box>
		</Box>
	);
};

RegistrationPageContext.propTypes = {
	tabs: PropTypes.object.isRequired,
	handleTabChange: PropTypes.func.isRequired,
	selectedTab: PropTypes.string.isRequired,
	siteDetails: PropTypes.object.isRequired,
	reCaptchaKey: PropTypes.string.isRequired,
	showCaptcha: PropTypes.bool,
	registrationCustomFields: PropTypes.array,
	csrfToken: PropTypes.string.isRequired,
	showRegisterWithGoogle: PropTypes.bool,
	googleUrl: PropTypes.string.isRequired,
};
RegistrationPageContext.defaultProps = {
	showCaptcha: true,
	registrationCustomFields: [],
	showRegisterWithGoogle: true,
};

export default RegistrationPageContext;
